import PropTypes from 'prop-types';
import { useContext } from 'react';
import { PiMagnifyingGlass, PiX } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import TableContext from '#/modules/commonComponents/table/TableContext';

const TableSearchInput = ({
  placeholder = '搜尋',
  className = '',
  keyword = '',
  onKeywordChange = null,
  onSearch = null,
  onClear = null,
  useKeyword = false,
  disabled = false,
}) => {
  const context = useContext(TableContext);
  const tableContext = context?.proxy ?? null;

  if (!useKeyword && !tableContext) {
    throw new Error('useTableContext must be used within a TableProvider');
  }

  const value = useKeyword ? keyword : tableContext.globalFilter;
  const onChange = useKeyword ? onKeywordChange : tableContext.setGlobalFilter;
  const hasValue = Boolean(String(value ?? '').length);

  function handleKeyDown(event) {
    if (event.key === 'Enter' && onSearch) {
      onSearch();
    }
  }

  function handleClear(event) {
    event.stopPropagation();
    if (onChange) {
      onChange('');
    }
    onClear?.();
  }

  return (
    <div className={cn('relative min-w-0', className)}>
      <PiMagnifyingGlass
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-500"
        aria-hidden
      />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange && onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        className={cn(
          'w-full p-2.5 pl-9 bg-[var(--surface-field)] border border-gray-200 rounded-md',
          hasValue && 'pr-9',
          disabled && [
            'bg-slate-100 text-muted-foreground',
            'opacity-70 cursor-not-allowed',
          ],
        )}
        disabled={disabled}
      />
      {hasValue && !disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-0.5 size-7 -translate-y-1/2 text-muted-foreground"
          onClick={handleClear}
          aria-label="清除搜尋"
        >
          <PiX className="size-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
};

TableSearchInput.propTypes = {
  placeholder: PropTypes.string,
  className: PropTypes.string,
  keyword: PropTypes.string,
  onKeywordChange: PropTypes.func,
  onSearch: PropTypes.func,
  /** 清除時額外回呼（例：左欄一併清空已送出的 keyword 並重查） */
  onClear: PropTypes.func,
  useKeyword: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default TableSearchInput;
